import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { HiChevronDown, HiCheck } from 'react-icons/hi';

export type SortOption = 'priority' | 'progress' | 'alphabetical' | 'stack_rank';

interface SortDropdownProps {
    value: SortOption;
    onChange: (value: SortOption) => void;
    showStackRank?: boolean;
}

const sortOptions: { value: SortOption; label: string }[] = [
    { value: 'stack_rank', label: 'Stack Rank' },
    { value: 'priority', label: 'Priority' },
    { value: 'progress', label: 'Progress' },
    { value: 'alphabetical', label: 'A-Z' },
];

const SortDropdown: React.FC<SortDropdownProps> = ({ value, onChange, showStackRank = false }) => {
    const buttonRef = useRef<HTMLButtonElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

    const visibleOptions = showStackRank
        ? sortOptions
        : sortOptions.filter(opt => opt.value !== 'stack_rank');

    const currentLabel = sortOptions.find(opt => opt.value === value)?.label || 'Priority';

    // Calculate dropdown position when opened
    useEffect(() => {
        if (isOpen && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom + window.scrollY + 4,
                left: rect.left + window.scrollX,
                width: Math.max(rect.width, 160)
            });
        }
    }, [isOpen]);

    // Close on outside click
    useEffect(() => {
        if (!isOpen) return;
        function handleClick(event: MouseEvent) {
            const target = event.target as Node;
            if (
                buttonRef.current && !buttonRef.current.contains(target) &&
                dropdownRef.current && !dropdownRef.current.contains(target)
            ) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, [isOpen]);

    const handleSelect = (optionValue: SortOption) => {
        onChange(optionValue);
        setIsOpen(false);
    };

    const dropdownContent = isOpen && (
        <div
            ref={dropdownRef}
            style={{
                position: 'fixed',
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                minWidth: dropdownPosition.width,
                zIndex: 99999
            }}
            className="bg-white dark:bg-secondary-dark border border-gray-300 dark:border-gray-600 rounded-lg shadow-xl overflow-hidden"
        >
            <div className="max-h-64 overflow-y-auto">
                {visibleOptions.map((opt) => {
                    const isSelected = value === opt.value;
                    return (
                        <button
                            key={opt.value}
                            type="button"
                            onClick={() => handleSelect(opt.value)}
                            className={`w-full px-3 py-2 text-sm cursor-pointer transition-colors duration-200
                         text-left text-gray-700 dark:text-gray-300 flex items-center gap-3
                         ${isSelected ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                        >
                            <span className="w-4 flex-shrink-0">
                                {isSelected && <HiCheck className="w-4 h-4 text-blue-500" />}
                            </span>
                            <span className="flex-1 truncate">{opt.label}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );

    return (
        <div className="relative">
            <button
                ref={buttonRef}
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                className={`w-[160px] px-3 py-2 text-left bg-white dark:bg-secondary-dark
                   border border-gray-300 dark:border-gray-600 rounded-lg text-sm
                   hover:border-gray-400 dark:hover:border-gray-500
                   focus:outline-none focus:ring-1 focus:ring-gray-300/30 dark:focus:ring-gray-400/30
                   text-gray-900 dark:text-white transition-colors duration-200 cursor-pointer h-[38px]`}
            >
                <div className="flex items-center justify-between h-full">
                    <span className="text-gray-900 dark:text-white truncate">
                        Sort: {currentLabel}
                    </span>
                    <HiChevronDown
                        className={`w-4 h-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ml-2 ${isOpen ? 'rotate-180' : ''}`}
                    />
                </div>
            </button>
            {typeof document !== 'undefined' && createPortal(dropdownContent, document.body)}
        </div>
    );
};

export default SortDropdown;
